import axios from 'axios';

/**
 * PAZARYERI (currency-management) HATA KODU -> KULLANICIYA GOSTERILEBILIR metin.
 *
 * Talep/teklif mutasyonlari (onay, iptal, uyari, "Odeme alindi", teklif verme, talep acma)
 * eskiden her ret icin `errors.generic` basiyordu. 2026-09-23 is kurallariyla sunucu artik
 * SEBEBI soyleyen sabit kodlar donuyor (OFFER_WINDOW_OPEN, WARNING_TOO_EARLY...) ve bunlarin
 * cogu kullanicinin "neden olmadi, ne zaman olur" sorusunun cevabi. Kodu oldugu gibi basmak
 * bir sey anlatmaz; once bu tabloya bakilir.
 *
 * DEGERLER METIN DEGIL ANAHTAR (bkz. features/reports/labels.ts basligi). Anahtarlarin
 * varligini src/i18n/__tests__/keys.test.ts dogruluyor.
 */
export const TASK_ERROR_MESSAGE_KEYS: Readonly<Record<string, string>> = Object.freeze({
  // Gecis pencereleri (task-status.js `window`)
  OFFER_WINDOW_OPEN: 'errors.offerWindowOpen',
  CANCEL_WINDOW_CLOSED: 'errors.cancelWindowClosed',
  WARNING_TOO_EARLY: 'errors.warningTooEarly',
  DISPUTE_WINDOW_CLOSED: 'errors.disputeWindowClosed',
  TASK_EXPIRED: 'errors.taskExpired',
  TASK_ALREADY_DECIDED: 'errors.taskAlreadyDecided',
  TASK_ALREADY_CANCELLED: 'errors.taskAlreadyCancelled',
  INVALID_STATUS_TRANSITION: 'errors.invalidStatusTransition',
  TASK_STATUS_REQUIRED: 'errors.taskStatusRequired',
  // Kapsam
  TASK_NOT_FOUND: 'errors.taskNotFound',
  TASK_NOT_OWNED: 'errors.taskNotOwned',
  TASK_NOT_WINNER: 'errors.taskNotWinner',
  PERMISSION_DENIED: 'errors.permissionDenied',
  // Teklif
  TASK_NOT_OPEN_FOR_OFFERS: 'errors.taskNotOpenForOffers',
  // Bir buro bir teklif (2026-10-03): is arkadasinin teklifi var, cagiranin `editOffer`'i yok.
  ORGANIZATION_OFFER_EXISTS: 'errors.organizationOfferExists',
  OFFER_WITHDRAW_NOT_SUPPORTED: 'errors.offerWithdrawNotSupported',
  OFFER_PRICE_TOO_PRECISE: 'offerFlow.amountTooPrecise',
  OFFER_PRICE_MUST_BE_POSITIVE: 'errors.offerPriceMustBePositive',
  OFFER_PRICE_TOO_LARGE: 'offerFlow.amountTooLarge',
  OFFER_PRICE_REQUIRED: 'offerFlow.amountInvalid',
  INVALID_OFFER_PRICE: 'offerFlow.amountInvalid',
  // Iki bacak / ayni banka (task-legs.js)
  ACCOUNT_LEG_MISMATCH: 'errors.accountLegMismatch',
  ACCOUNT_COUNTER_LEG_MISSING: 'errors.accountCounterLegMissing',
});

/**
 * Sunucunun yazdigi kodu cikarir. HTTP 4xx'te axios firlatir ve kod govdededir; HTTP 200 +
 * `code: 'error'` durumunda `unwrap` bir ApiError firlatir ve KOD onun MESAJIDIR.
 * (features/admin/error-message.ts ile ayni iki yol.)
 */
const rawServerCode = (error: unknown): string | null => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: unknown } | undefined;
    return typeof data?.message === 'string' ? data.message : null;
  }
  if (error instanceof Error) return error.message;
  return null;
};

/**
 * Tabloda olmayan kod ham BASILMAZ: bu servis Turkce mesaj yazmiyor, 'UNSUCCESSFULLY_TASK_CREATE'
 * gibi bir metin kullaniciya hicbir sey anlatmaz. Ekranin kendi genel mesaji (`fallbackKey`)
 * gosterilir. Ag hatasi ve 429 ayri: onlar kullanicinin yapabilecegi bir seyi soyluyor.
 *
 * `overrides`: AYNI kodun o ekranda daha dogru bir anlami varsa (kod -> anahtar). Ornek: talep
 * detayinda secim istemci sayacina gore acildiktan sonra gelen 409 OFFER_WINDOW_OPEN "teklifler
 * hala toplaniyor" degil, "sunucu saati birkac saniye geride" demek (2026-10-02, M2).
 */
export const taskErrorMessage = (
  error: unknown,
  t: (key: string) => string,
  fallbackKey = 'errors.generic',
  overrides: Readonly<Record<string, string>> = {},
): string => {
  const raw = rawServerCode(error);
  const key = raw === null ? undefined : (overrides[raw] ?? TASK_ERROR_MESSAGE_KEYS[raw]);
  if (key !== undefined) return t(key);
  if (axios.isAxiosError(error)) {
    if (!error.response) return t('auth.networkError');
    if (error.response.status === 429) return t('auth.tooManyAttempts');
  }
  return t(fallbackKey);
};
